import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, useLocation} from "react-router-dom";

import {useEntidad, useForm, useFormErrors, useReporte, useNav, useNavigateWithFilters, useUnsavedChangesGuard} from '../../components/hooks';
import {
    Loading,
    SectionHeading,
    DatePickerCustom,
    InputEntidad,
    InputExpediente,
    InputPersona,
    Accordion,
    FormError,
    FormLabel
} from '../../components/common';
import {FooterButtonVolver} from '../../components/buttons/FooterButtonVolver/FooterButtonVolver';
import {CloneObject, OpenObjectURL} from '../../utils/helpers';
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, dataTaggerActionClear, dataTaggerActionSet } from '@laramie/ui';
import {APIS} from '../../config/apis';
import {ServerRequest} from '../../utils/apiweb';
import {getDateId, getDateNow} from '../../utils/convert';
import DataTaggerFormRedux from '../../components/controls/DataTaggerFormRedux';
import {Tab, Tabs} from '../../components/common/TabCustom';
import CertificadosApremiosGrid from '../../components/controls/CertificadosApremiosGrid';
import JuiciosCitacionesGrid from '../../components/controls/JuiciosCitacionesGrid';
import ActosProcesalesGrid from '../../components/controls/ActosProcesalesGrid';
import {isEmptyString} from '../../utils/validator';
import {usePermissions} from '../../components/hooks/usePermissions';
import {PERMISSIONS} from '../../consts/permissions';
import {TIPS} from "../../consts/tips";
import { VALIDATION_MESSAGE } from '../../consts/validationMessage';

const ApremioView = (props) => {

    //parametros url
    const params = useParams();
    const location = useLocation();
    const previousFilters = location.state?.filters;

    //variables
    const entityInit = {
        apremio: {
            id: 0,
            numero: '',
            idExpediente: 0,
            detalleExpediente: '',
            idOrganismoJudicial: 0,
            fechaInicioDemanda: null,
            carpeta: '',
            caratula: '',
            estado: '',
            idPersonaAbogado: 0,
            idTipoPersonaAbogado: 0,
            nombrePersonaAbogado: '',
            idTipoDocumentoAbogado: 0,
            numeroDocumentoAbogado: ''
        },
        certificadosApremio: [],
        juicioCitaciones: [],
        actosProcesales: [],
        archivos: [],
        observaciones: [],
        etiquetas: []
    };

    //hooks
    const navigate = useNav()
    const navigateWithFilters = useNavigateWithFilters(() => previousFilters || null);

    const [state, setState] = useState({
        processKey: `Apremio_${params.id ?? 0}_${getDateId()}`,
        id: params.id ? parseInt(params.id) : 0,
        idCertificadoApremio: params.mode === 'new' ? parseInt(params.id) : 0,
        mode: params.mode,
        loading: false,
        entity: entityInit,
        certificadosApremio: [],
        tabActive: "actosProcesales"
    });
    const [pendingChange, setPendingChange] = useState(false);
    const [stateOrganismoJudicial, setStateOrganismoJudicial] = useState({
        fueroOrganismoJudicial: '',
        secretariaOrganismoJudicial: '',
    });

    const [personaAbogado, setPersonaAbogado] = useState({
        idTipoPersonaAbogado: 0,
        nombrePersonaAbogado: "",
        numeroDocumentoAbogado: "",
        idTipoDocumentoAbogado: 0
    });

    //al generar el apremio se propone el abogado/a del certificado o, si no tiene, el abogado/a por defecto
    const [certificadoApremioPersonas, setCertificadoApremioPersonas] = useState(null);
    const [abogadoPropuesto, setAbogadoPropuesto] = useState(false);

    const [expediente, setExpediente] = useState({
        detalleExpediente: ""
    });

    const dispatch = useDispatch();
    const dataTagger = useSelector((state) => state.dataTagger.data);

    const {allowNavigation} = useUnsavedChangesGuard(pendingChange);

    const {hasPermission} = usePermissions();
    const canViewCertificadoApremio = hasPermission(PERMISSIONS.CERTIFICADO_APREMIO_VIEW);

    const mount = () => {
        if (state.mode != OPERATION_MODE.NEW) {
            FindApremio();
        } else if (state.idCertificadoApremio ?? 0 > 0) {
            dispatch(dataTaggerActionSet(state.processKey, {
                Archivo: [],
                Observacion: [],
                Etiqueta: []
            }));
            FindCertificadoApremio(state.idCertificadoApremio);
        }
    }
    useEffect(mount, []);

    const [personasControladores, setPersonasControladores] = useState([]);

    const [getListEntidad, getRowEntidad, readyEntidad] = useEntidad({
        entidades: ['OrganoJudicial', 'TipoControlador', 'Controlador', 'TipoRelacionCertificadoApremioPersona'],
        onLoaded: (entidades, isSuccess, error) => {
            if (isSuccess) {
                setState(prevState => ({...prevState}));
            } else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: 'OrganoJudicial_TipoControlador_Controlador_TipoRelacionCertificadoApremioPersona',
            timeout: 0
        }
    });

    useEffect(() => {
        const idsTipoControladorAbogado = getListEntidad('TipoControlador').filter(f => f.abogado || f.oficialJusticia).map(x => x.id);
        const idsPersonaAbogado = getListEntidad('Controlador').filter(f => idsTipoControladorAbogado.includes(f.idTipoControlador)).map(x => x.idPersona);
        setPersonasControladores(idsPersonaAbogado); //las personas que son abogados u oficial de justicia
    }, [readyEntidad]);

    useEffect(() => {
        if (state.mode !== OPERATION_MODE.NEW || !readyEntidad || certificadoApremioPersonas === null || abogadoPropuesto) return;

        const abogado = getAbogadoPropuesto(certificadoApremioPersonas);
        if (abogado) {
            apremio_formSet({...apremio_formValues, idPersonaAbogado: abogado.idPersona});
            setPersonaAbogado({
                idTipoPersonaAbogado: abogado.idTipoPersona,
                nombrePersonaAbogado: abogado.nombrePersona,
                numeroDocumentoAbogado: abogado.numeroDocumento,
                idTipoDocumentoAbogado: abogado.idTipoDocumento
            });
        }
        setAbogadoPropuesto(true);
    }, [readyEntidad, certificadoApremioPersonas]);

    const mountOrganistmoJudicial = () => {
        if (readyEntidad && state.entity.apremio.idOrganismoJudicial > 0) {
            setStateOrganismoJudicial({
                fueroOrganismoJudicial: getDescFuero(state.entity.apremio.idOrganismoJudicial),
                secretariaOrganismoJudicial: getDescSecretaria(state.entity.apremio.idOrganismoJudicial)
            });
        }
    }
    useEffect(mountOrganistmoJudicial, [state.entity, readyEntidad]);

    const [apremio_formValues, apremio_formHandle, , apremio_formSet] = useForm({
        numero: '',
        idExpediente: 0,
        idOrganismoJudicial: 0,
        fechaInicioDemanda: null,
        carpeta: '',
        caratula: '',
        estado: '',
        idPersonaAbogado: 0
    }, 'apremio_');

    const [generateReporte,] = useReporte({
        callback: (reporte, buffer, message) => {
            if (buffer)
                OpenObjectURL(`${reporte}.pdf`, buffer);
            else
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        }
    });

    const getAbogadoPropuesto = (personasCertificado) => {
        const idsTipoControladorAbogado = getListEntidad('TipoControlador').filter(f => f.abogado).map(x => x.id);
        const idsTipoRelacionAbogado = getListEntidad('TipoRelacionCertificadoApremioPersona').filter(f => idsTipoControladorAbogado.includes(f.idTipoControlador)).map(x => x.id);
        const abogadoCertificado = personasCertificado.find(f => idsTipoRelacionAbogado.includes(f.idTipoRelacionCertificadoApremioPersona));
        if (abogadoCertificado) return abogadoCertificado;

        const abogadoPorDefecto = getListEntidad('Controlador').find(f => f.abogadoPorDefecto && idsTipoControladorAbogado.includes(f.idTipoControlador) && isVigente(f));
        return abogadoPorDefecto ?? null;
    }

    //vigente = con alta y sin baja a la fecha, comparando sólo el día
    const isVigente = (controlador) => {
        const hoy = getDateNow(false);
        const dia = (fecha) => new Date(new Date(fecha).toDateString());
        return (!controlador.fechaAlta || dia(controlador.fechaAlta) <= hoy) && (!controlador.fechaBaja || dia(controlador.fechaBaja) > hoy);
    }

    const getDescSecretaria = (id) => {
        const row = getRowEntidad('OrganoJudicial', id);
        return (row) ? row.secretaria : '';
    }

    const getDescFuero = (id) => {
        const row = getRowEntidad('OrganoJudicial', id);
        return (row) ? row.fuero : '';
    }

    //handles
    const apremio_formHandleProxy = (event) => {
        apremio_formHandle(event);
        setPendingChange(true);
    }
    const [apremioOpen, setApremioOpen] = useState(true);

    const { errorOf, validate } = useFormErrors(getErrors);

    const handleClickGuardar = () => {
        if (getErrors().fechaInicioDemanda) setApremioOpen(true);

        if (validate()) {
            if (state.mode === OPERATION_MODE.NEW) {
                AddApremio();
            } else {
                ModifyApremio();
            }
        }
        ;
    };

    const handleClickReporte = (reporte) => {
        const paramsReporte = {
            idApremio: state.id
        }
        generateReporte(reporte, paramsReporte);
    }

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                const message = error.message;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
    }
    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => {
            return {...prevState, loading: false};
        });
    }
    const callbackSuccessFindApremio = (response) => {
        response.json()
            .then((data) => {

                //deserialize date fields
                if (data.apremio.fechaInicioDemanda) data.apremio.fechaInicioDemanda = new Date(data.apremio.fechaInicioDemanda);
                data.archivos.forEach(x => {
                    if (x.fecha) x.fecha = new Date(x.fecha);
                });
                data.observaciones.forEach(x => {
                    if (x.fecha) x.fecha = new Date(x.fecha);
                });
                data.actosProcesales.forEach(x => {
                    if (x.fechaDesde) x.fechaDesde = new Date(x.fechaDesde);
                    if (x.fechaHasta) x.fechaHasta = new Date(x.fechaHasta);
                });
                data.juicioCitaciones.forEach(x => {
                    if (x.fechaCitacion) x.fechaCitacion = new Date(x.fechaCitacion);
                });
                data.certificadosApremio.forEach(x => {
                    if (x.fechaCalculo) x.fechaCalculo = new Date(x.fechaCalculo);
                    if (x.fechaCertificado) x.fechaCertificado = new Date(x.fechaCertificado);
                    if (x.fechaNotificacion) x.fechaNotificacion = new Date(x.fechaNotificacion);
                    if (x.fechaRecepcion) x.fechaRecepcion = new Date(x.fechaRecepcion);
                });

                setState(prevState => {
                    return {...prevState, loading: false, entity: data};
                });
                setPendingChange(false);
                apremio_formSet({
                    numero: data.apremio.numero,
                    idExpediente: data.apremio.idExpediente,
                    idOrganismoJudicial: data.apremio.idOrganismoJudicial,
                    fechaInicioDemanda: data.apremio.fechaInicioDemanda,
                    carpeta: data.apremio.carpeta,
                    caratula: data.apremio.caratula,
                    estado: data.apremio.estado,
                    idPersonaAbogado: data.apremio.idPersonaAbogado
                });
                setPersonaAbogado({
                    idTipoPersonaAbogado: data.apremio.idTipoPersonaAbogado,
                    nombrePersonaAbogado: data.apremio.nombrePersonaAbogado,
                    numeroDocumentoAbogado: data.apremio.numeroDocumentoAbogado,
                    idTipoDocumentoAbogado: data.apremio.idTipoDocumentoAbogado
                });
                setExpediente({
                    detalleExpediente: data.apremio.detalleExpediente
                });

                if (data.apremio.id > 0) {
                    SearchCertificadoApremio(data.apremio.id);
                }

                dispatch(dataTaggerActionSet(state.processKey, {
                    Archivo: data.archivos,
                    Observacion: data.observaciones,
                    Etiqueta: data.etiquetas
                }));
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => {
                    return {...prevState, loading: false};
                });
            });
    };

    //funciones
    function getErrors() {
        const errors = {};

        if (apremio_formValues.fechaInicioDemanda == null) errors.fechaInicioDemanda = VALIDATION_MESSAGE.REQUIRED_DATE;

        return errors;
    }

    function FindApremio() {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const paramsUrl = `/${state.id}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.APREMIO,
            paramsUrl,
            null,
            callbackSuccessFindApremio,
            callbackNoSuccess,
            callbackError
        );

    }

    function FindCertificadoApremio(idCertificadoApremio) {
        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
                .then((data) => {
                    setState(prevState => {
                        return {...prevState, loading: false, certificadosApremio: [data.certificadoApremio]};
                    });
                    setCertificadoApremioPersonas(data.certificadoApremioPersonas ?? []);
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => {
                        return {...prevState, loading: false};
                    });
                });
        };

        const paramsUrl = `/${idCertificadoApremio}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CERTIFICADO_APREMIO,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    function SearchCertificadoApremio(idApremio) {
        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
                .then((data) => {
                    setState(prevState => {
                        return {...prevState, loading: false, certificadosApremio: data};
                    });
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => {
                        return {...prevState, loading: false};
                    });
                });
        };

        const paramsUrl = `/filter?idCuenta=0&idApremio=${idApremio}&numero=&idEstadoCertificadoApremio=0&fechaCertificadoDesde=&fechaCertificadoHasta=`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.CERTIFICADO_APREMIO,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    function AddApremio() {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            response.json()
                .then((row) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Apremio ingresado correctamente", () => {
                        dispatch(dataTaggerActionClear(state.processKey));
                        setState(prevState => {
                            return {...prevState, loading: false};
                        });
                        allowNavigation();
                        navigate({
                            to: `/apremio/${OPERATION_MODE.EDIT}/${row.apremio.id}`,
                            refresh: true,
                            replace: true,
                        })
                    });
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => {
                        return {...prevState, loading: false};
                    });
                });
        };

        const dataBody = {
            apremio: {
                ...state.entity.apremio,
                numero: apremio_formValues.numero,
                idExpediente: !isEmptyString(apremio_formValues.idExpediente) ? parseInt(apremio_formValues.idExpediente) : 0,
                detalleExpediente: expediente.detalleExpediente,
                idOrganismoJudicial: !isEmptyString(apremio_formValues.idOrganismoJudicial) ? parseInt(apremio_formValues.idOrganismoJudicial) : 0,
                fechaInicioDemanda: apremio_formValues.fechaInicioDemanda,
                carpeta: apremio_formValues.carpeta,
                caratula: apremio_formValues.caratula,
                estado: apremio_formValues.estado,
                idPersonaAbogado: parseInt(apremio_formValues.idPersonaAbogado),
                idTipoPersonaAbogado: parseInt(personaAbogado.idTipoPersonaAbogado),
                nombrePersonaAbogado: personaAbogado.nombrePersonaAbogado,
                idTipoDocumentoAbogado: parseInt(personaAbogado.idTipoDocumentoAbogado),
                numeroDocumentoAbogado: personaAbogado.numeroDocumentoAbogado
            },
            certificadosApremio: state.entity.certificadosApremio,
            juicioCitaciones: state.entity.juicioCitaciones.filter(f => f.state !== 'o'),
            actosProcesales: state.entity.actosProcesales.filter(f => f.state !== 'o'),
            archivos: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Archivo.filter(f => f.state !== 'o') : [],
            observaciones: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Observacion.filter(f => f.state !== 'o') : [],
            etiquetas: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Etiqueta.filter(f => f.state !== 'o') : []
        };

        const paramsUrl = `/${state.id}`;

        ServerRequest(
            REQUEST_METHOD.POST,
            null,
            true,
            APIS.URLS.APREMIO,
            paramsUrl,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );

    }

    function ModifyApremio() {

        setState(prevState => {
            return {...prevState, loading: true};
        });

        const callbackSuccess = (response) => {
            ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Apremio actualizado correctamente", () => {
                dispatch(dataTaggerActionClear(state.processKey));
                callbackSuccessFindApremio(response);
            });
        };

        const dataBody = {
            apremio: {
                ...state.entity.apremio,
                numero: apremio_formValues.numero,
                idExpediente: !isEmptyString(apremio_formValues.idExpediente) ? parseInt(apremio_formValues.idExpediente) : 0,
                detalleExpediente: expediente.detalleExpediente,
                idOrganismoJudicial: !isEmptyString(apremio_formValues.idOrganismoJudicial) ? parseInt(apremio_formValues.idOrganismoJudicial) : 0,
                fechaInicioDemanda: apremio_formValues.fechaInicioDemanda,
                carpeta: apremio_formValues.carpeta,
                caratula: apremio_formValues.caratula,
                estado: apremio_formValues.estado,
                idPersonaAbogado: parseInt(apremio_formValues.idPersonaAbogado),
                idTipoPersonaAbogado: parseInt(personaAbogado.idTipoPersonaAbogado),
                nombrePersonaAbogado: personaAbogado.nombrePersonaAbogado,
                idTipoDocumentoAbogado: parseInt(personaAbogado.idTipoDocumentoAbogado),
                numeroDocumentoAbogado: personaAbogado.numeroDocumentoAbogado
            },
            certificadosApremio: state.entity.certificadosApremio,
            juicioCitaciones: state.entity.juicioCitaciones.filter(f => f.state !== 'o'),
            actosProcesales: state.entity.actosProcesales.filter(f => f.state !== 'o'),
            archivos: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Archivo.filter(f => f.state !== 'o') : [],
            observaciones: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Observacion.filter(f => f.state !== 'o') : [],
            etiquetas: (dataTagger[state.processKey]) ? dataTagger[state.processKey].Etiqueta.filter(f => f.state !== 'o') : []
        };

        const paramsUrl = `/${state.id}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.APREMIO,
            paramsUrl,
            dataBody,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    function UnbindCertificado(idCertificado) {
        setState(prevState => {
            return {...prevState, loading: true};
        });

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CERTIFICADO_APREMIO,
            `/apremio/unbind/${idCertificado}`,
            null,
            () => SearchCertificadoApremio(state.id),
            callbackNoSuccess,
            callbackError
        )
    }


    function UpdateEntity(typeEntity, row) {
        // o:original a:add m:modify r:remove c:modify only childs

        let entity = CloneObject(state.entity);

        if (typeEntity === 'ActoProcesal') {
            if (row.state === 'a') {
                entity.actosProcesales.push(row);
            } else if (row.state === 'm') {
                const index = entity.actosProcesales.indexOf(entity.actosProcesales.find(x => x.id === row.id));
                if (index !== -1) {
                    if (row.id < 0) row.state = 'a'; //originalmente era un alta
                    entity.actosProcesales[index] = row;
                }
            } else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.actosProcesales = entity.actosProcesales.filter(f => f.id !== row.id);
                } else {
                    let item = entity.actosProcesales.find(x => x.id === row.id);
                    item.state = 'r';
                }
            }
        }

        if (typeEntity === 'JuicioCitacion') {
            if (row.state === 'a') {
                entity.juicioCitaciones.push(row);
            } else if (row.state === 'm') {
                const index = entity.juicioCitaciones.indexOf(entity.juicioCitaciones.find(x => x.id === row.id));
                if (index !== -1) {
                    if (row.id < 0) row.state = 'a'; //originalmente era un alta
                    entity.juicioCitaciones[index] = row;
                }
            } else if (row.state === 'r') {
                if (row.id < 0) { //lo elimino directamente porque no existe en la bd
                    entity.juicioCitaciones = entity.juicioCitaciones.filter(f => f.id !== row.id);
                } else {
                    let item = entity.juicioCitaciones.find(x => x.id === row.id);
                    item.state = 'r';
                }
            }
        }

        setState(prevState => {
            return {...prevState, entity: entity};
        });
        setPendingChange(true);
    }

    function SetTabActive(tab) {
        setState(prevState => {
            return {...prevState, tabActive: tab};
        });
    }

    return (
        <>

            <Loading visible={state.loading}></Loading>


            <div className='section-frames'>

                <div className='section-main'>

                    <SectionHeading
                        titles={[
                            {title: 'Legales'},
                            {title: 'Apremios', url: '/apremios',},
                            {title: params.mode === OPERATION_MODE.NEW ? 'Nuevo' : `Número: ${apremio_formValues.numero}`}
                        ]}
                    />

                    <section className='section-accordion'>
                        <div className="m-top-20 m-bottom-20">
                            <Accordion title="Información del apremio" open={apremioOpen} onToggle={setApremioOpen}>
                                <div className="row">
                                    {state.mode !== OPERATION_MODE.NEW &&
                                        <div className="mb-3 col-6 col-md-2">
                                            <label htmlFor="numero" className="form-label">Apremio</label>
                                            <input
                                                name="numero"
                                                type="text"
                                                placeholder=""
                                                className="form-control"
                                                value={apremio_formValues.numero}
                                                onChange={apremio_formHandleProxy}
                                                disabled={true}
                                            />
                                        </div>
                                    }
                                    <div className="mb-3 col-6 col-md-2">
                                        <FormLabel htmlFor="fechaInicioDemanda" required>Fecha inicio de demanda</FormLabel>
                                        <DatePickerCustom
                                            name="fechaInicioDemanda"
                                            placeholder=""
                                            className={`form-control ${errorOf('fechaInicioDemanda') ? 'is-invalid' : ''}`}
                                            value={apremio_formValues.fechaInicioDemanda}
                                            onChange={apremio_formHandleProxy}
                                            disabled={state.mode === OPERATION_MODE.VIEW}
                                        />
                                        <FormError message={errorOf('fechaInicioDemanda')} />
                                    </div>
                                    <div className="mb-3 col-6 col-md-3">
                                        <label htmlFor="idExpediente" className="form-label">Expediente</label>
                                        <InputExpediente
                                            name="idExpediente"
                                            placeholder=""
                                            className="form-control"
                                            value={apremio_formValues.idExpediente}
                                            onChange={(event) => {
                                                const {target} = event;
                                                const expediente = (target.row) ? {
                                                    detalleExpediente: target.row.detalleExpediente,
                                                } : {
                                                    detalleExpediente: ""
                                                };
                                                setExpediente(expediente);
                                                apremio_formHandleProxy(event);
                                            }}
                                            disabled={state.mode === OPERATION_MODE.VIEW}
                                        />
                                    </div>
                                    <div className="mb-3 col-6 col-md-5">
                                        <label htmlFor="idPersonaAbogado" className="form-label">Abogado/a</label>
                                        <InputPersona
                                            name="idPersonaAbogado"
                                            placeholder=""
                                            className="form-control"
                                            value={apremio_formValues.idPersonaAbogado}
                                            idTipoPersona={0}
                                            onChange={(event) => {
                                                const {target} = event;
                                                const persona = (target.row) ? {
                                                    idTipoPersonaAbogado: target.row.idTipoPersona,
                                                    idTipoDocumentoAbogado: target.row.idTipoDocumento,
                                                    numeroDocumentoAbogado: target.row.numeroDocumento,
                                                    nombrePersonaAbogado: target.row.nombrePersona
                                                } : {
                                                    idTipoPersonaAbogado: 0,
                                                    idTipoDocumentoAbogado: 0,
                                                    numeroDocumentoAbogado: "",
                                                    nombrePersonaAbogado: ""
                                                };
                                                setPersonaAbogado(persona);
                                                apremio_formHandleProxy(event);
                                            }}
                                            filter={(row) => personasControladores.includes(row.id)}
                                            disabled={state.mode === OPERATION_MODE.VIEW}
                                        />
                                    </div>
                                    <div className="mb-3 col-6 col-md-4">
                                        <label htmlFor="idOrganismoJudicial" className="form-label">Organismo
                                            judicial</label>
                                        <InputEntidad
                                            name="idOrganismoJudicial"
                                            tipName={TIPS.NAME.MODALS.ORGANISMO_JUDICIAL_MODAL}
                                            placeholder=""
                                            className="form-control"
                                            value={apremio_formValues.idOrganismoJudicial}
                                            onChange={({target}) => {
                                                let idOrganismoJudicial = 0;
                                                let fuero = '';
                                                let secretaria = '';
                                                if (target.row) {
                                                    idOrganismoJudicial = parseInt(target.value);
                                                    fuero = target.row.fuero;
                                                    secretaria = target.row.secretaria;
                                                }
                                                apremio_formSet({
                                                    ...apremio_formValues,
                                                    idOrganismoJudicial: idOrganismoJudicial
                                                });

                                                setStateOrganismoJudicial(prevState => {
                                                    return {
                                                        ...prevState,
                                                        fueroOrganismoJudicial: fuero,
                                                        secretariaOrganismoJudicial: secretaria
                                                    };
                                                });
                                                setPendingChange(true);
                                            }}
                                            disabled={state.mode === OPERATION_MODE.VIEW}
                                            title="Organismo judicial"
                                            entidad="OrganoJudicial"
                                            onFormat={(row) => (row) ? `${row.codigoOrganoJudicial} - ${row.departamentoJudicial}` : ''}
                                            columns={[
                                                {Header: 'Código', accessor: 'codigoOrganoJudicial', width: '25%'},
                                                {
                                                    Header: 'Departamento judicial',
                                                    accessor: 'departamentoJudicial',
                                                    width: '50%'
                                                },
                                                {Header: 'Secretaría', accessor: 'secretaria', width: '25%'}
                                            ]}
                                            memo={false}
                                        />
                                    </div>
                                    <div className="mb-3 col-6 col-md-3">
                                        <label htmlFor="fueroOrganismoJudicial" className="form-label">Fuero</label>
                                        <input
                                            name="fueroOrganismoJudicial"
                                            type="text"
                                            placeholder=""
                                            className="form-control"
                                            value={stateOrganismoJudicial.fueroOrganismoJudicial}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className="mb-3 col-6 col-md-3">
                                        <label htmlFor="secretariaOrganismoJudicial"
                                               className="form-label">Secretaría</label>
                                        <input
                                            name="secretariaOrganismoJudicial"
                                            type="text"
                                            placeholder=""
                                            className="form-control"
                                            value={stateOrganismoJudicial.secretariaOrganismoJudicial}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className="mb-3 col-12 col-md-2">
                                        <label htmlFor="carpeta" className="form-label">Carpeta</label>
                                        <input
                                            name="carpeta"
                                            type="text"
                                            placeholder=""
                                            className="form-control"
                                            value={apremio_formValues.carpeta}
                                            onChange={apremio_formHandleProxy}
                                            disabled={state.mode === OPERATION_MODE.VIEW}
                                        />
                                    </div>
                                    <div className="mb-3 col-12 col-md-4">
                                        <label htmlFor="caratula" className="form-label">Carátula</label>
                                        <input
                                            name="caratula"
                                            type="text"
                                            placeholder=""
                                            className="form-control"
                                            value={apremio_formValues.caratula}
                                            onChange={apremio_formHandleProxy}
                                            disabled={state.mode === OPERATION_MODE.VIEW}
                                        />
                                    </div>
                                    <div className="mb-3 col-12 col-md-2">
                                        <label htmlFor="estado" className="form-label">Estado</label>
                                        <input
                                            name="estado"
                                            type="text"
                                            placeholder=""
                                            className="form-control"
                                            value={apremio_formValues.estado}
                                            onChange={apremio_formHandleProxy}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </Accordion>

                            <div className="m-top-20">
                                <Accordion title="Certificados">
                                    <CertificadosApremiosGrid
                                        data={{
                                            idCertificadoApremio: state.entity.apremio.id,
                                            list: state.certificadosApremio
                                        }}
                                        allowViewDetail={canViewCertificadoApremio}
                                        allowUnbind={(state.mode !== OPERATION_MODE.VIEW)}
                                        onUnbind={UnbindCertificado}
                                    />
                                </Accordion>
                            </div>

                            <div className='m-top-20'>
                                <Accordion title="Información adicional">
                                    <DataTaggerFormRedux
                                        title="Información adicional de Apremio"
                                        processKey={state.processKey}
                                        entidad="Apremio"
                                        idEntidad={state.id}
                                        disabled={(state.mode === OPERATION_MODE.VIEW)}
                                        onChange={(row) => setPendingChange(true)}
                                    />
                                </Accordion>
                            </div>

                            {state.mode !== OPERATION_MODE.NEW &&
                                <Tabs
                                    id="tabs-inmueble"
                                    activeKey={state.tabActive}
                                    className="m-top-20"
                                    onSelect={(tab) => SetTabActive(tab)}
                                >
                                    <Tab eventKey="actosProcesales" title="Actos procesales">
                                        <div className='tab-panel'>
                                            <ActosProcesalesGrid
                                                processKey={state.processKey}
                                                disabled={state.mode === OPERATION_MODE.VIEW}
                                                data={{
                                                    idApremio: state.entity.apremio.id,
                                                    list: state.entity.actosProcesales
                                                }}
                                                onChange={(typeEntity, row) => UpdateEntity(typeEntity, row)}
                                            />
                                        </div>
                                    </Tab>
                                    <Tab eventKey="citaciones" title="Citaciones">
                                        <div className='tab-panel'>
                                            <JuiciosCitacionesGrid
                                                processKey={state.processKey}
                                                disabled={state.mode === OPERATION_MODE.VIEW}
                                                data={{
                                                    idApremio: state.entity.apremio.id,
                                                    list: state.entity.juicioCitaciones
                                                }}
                                                onChange={(typeEntity, row) => UpdateEntity(typeEntity, row)}
                                            />
                                        </div>
                                    </Tab>
                                </Tabs>
                            }
                        </div>

                    </section>

                </div>
            </div>

            <footer className='footer footer-action'>
                <div className='footer-action-container'>
                    <FooterButtonVolver
                        pendingChange={pendingChange}
                        processKey={state.processKey}
                        filters={previousFilters}
                        to={state.mode === OPERATION_MODE.NEW ? `/certificado-apremio/${OPERATION_MODE.EDIT}/${state.id}` : '/apremios'}
                        allowNavigation={allowNavigation}
                    />
                    {state.mode !== OPERATION_MODE.VIEW &&
                        <>
                            {/* {state.mode !== OPERATION_MODE.NEW &&
                    <button className="btn task-button m-left-10 float-start" onClick={ (event) => handleClickReporte("ApremioCaratula") }>Imprimir carátula</button>
                    } */}
                            <button
                                className={pendingChange ? "btn action-button float-end" : "btn action-button btn-disabled float-end"}
                                onClick={(event) => handleClickGuardar()}
                                disabled={!pendingChange}
                            >
                                Guardar
                            </button>
                        </>
                    }
                </div>
            </footer>

        </>
    );
}


export default ApremioView;